/* ====== Basic slider event handling
 * [1. Navigation buttons] [2. Pagination buttons] ====== */
class SliderBasicHandler {
  constructor(slider) {
    this.slider = slider;
    this.prevButton = this.slider.querySelector('button[name="previous"]');
    this.nextButton = this.slider.querySelector('button[name="next"]');
    this.pageDotsWrapper = this.slider.querySelector(".slider-page-dots");
    this.counterWrapper = this.slider.querySelector(".slider-counter");
    this.initModule();
  }

  /* ====== Navigation button click ====== */
  onButtonClick(direction) {
    this.slider.slideByDirection(direction);
  }

  /* ====== Pagination button click ====== */
  onPageLinkClick(event) {
    const page = parseInt(event.currentTarget.dataset.page);
    this.slider.slideByPage(page);
  }

  initModule() {
    /* ====== Create and bind event handler ====== */
    const onButtonClickFunction = webvista.debounce(
      this.onButtonClick.bind(this),
      200,
    );
    this.boundOnButtonClick = (event) => {
      const direction = event.currentTarget.name;
      onButtonClickFunction(direction);
    };

    // Get total page count
    this.pageCount = this.slider.slideLength;
    if (!this.slider.ifLoopingInfinite) {
      this.pageCount = this.slider.slideLength - this.slider.slidesPerPage + 1;
    }

    // Navigation buttons
    if (this.prevButton)
      this.prevButton.addEventListener("click", this.boundOnButtonClick);
    if (this.nextButton)
      this.nextButton.addEventListener("click", this.boundOnButtonClick);

    // Create pagination dots
    if (this.pageDotsWrapper) this.initPageDots();

    // Create page counter display
    if (this.counterWrapper) this.initCounter();

    // Support left/right arrow key navigation
    this.slider.addEventListener("keydown", (event) => {
      const key = event.code ? event.code.toUpperCase() : "";

      if (key === "ARROWLEFT") {
        event.preventDefault();
        this.slider.slideByDirection("previous");
      }

      if (key === "ARROWRIGHT") {
        event.preventDefault();
        this.slider.slideByDirection("next");
      }
    });
  }

  /* ====== Initialize pagination dots ====== */
  initPageDots() {
    if (!this.pageDotsWrapper) return;
    this.boundOnPageButtonClick = this.onPageLinkClick.bind(this);

    for (let i = 1; i <= this.pageCount; i++) {
      const link = document.createElement("li");
      link.className = "page-dot";
      link.setAttribute("data-page", i);
      link.setAttribute("tabindex", "0");
      if (this.pageDotsWrapper.hasAttribute("data-number"))
        link.innerText = i.toString();

      link.addEventListener("click", this.boundOnPageButtonClick);
      // Add keyboard event
      link.addEventListener("keydown", (event) => {
        const key = event.code ? event.code.toUpperCase() : "";
        if (key === "ENTER" || key === "SPACE") {
          event.preventDefault();
          this.boundOnPageButtonClick(event);
        }
      });

      this.pageDotsWrapper.appendChild(link);
    }
  }

  /* ====== Initialize page counter display ====== */
  initCounter() {
    const totalCounterElement =
      this.counterWrapper.querySelector(".total-page");
    if (totalCounterElement) totalCounterElement.innerText = this.pageCount;
  }

  /* ====== Update basic module info after slide change ====== */
  updateInfo(currentSlide = null, totalPage = 1) {
    if (!currentSlide) return;

    const currentPage = currentSlide.dataset.page;
    // Pagination dots
    if (this.pageDotsWrapper) {
      this.pageDotsWrapper.querySelector("li.current")?.classList.remove("current");
      this.pageDotsWrapper.querySelector(`li[data-page="${currentPage}"]`)?.classList.add("current");
    }

    // Current page counter
    if (this.counterWrapper) {
      const currentCounterElement =
        this.counterWrapper.querySelector(".current-page");
      if (currentCounterElement) {
        currentCounterElement.innerText = currentPage;
      }
    }

    // Navigation buttons disabled state
    if (!this.slider.ifEnableLooping) {
      this.prevButton &&
        this.prevButton.toggleAttribute("disabled", currentPage <= 1);
      this.nextButton &&
        this.nextButton.toggleAttribute("disabled", currentPage >= totalPage);
    }
  }

  /* ====== Unmount module ====== */
  unmount() {
    if (this.prevButton)
      this.prevButton.removeEventListener("click", this.boundOnButtonClick);
    if (this.nextButton)
      this.nextButton.removeEventListener("click", this.boundOnButtonClick);

    if (this.pageDotsWrapper) this.pageDotsWrapper.innerHTML = "";
  }
}

/* ======
 * SliderAutoplay manages the autoplay functionality of the slider.
 * It starts or pauses playback based on the slider's visibility and user
 * interactions such as mouse hover or focus events.
 * Uses the Intersection Observer API to detect viewport visibility
 * and control autoplay accordingly. ====== */
class SliderAutoplay {
  /**
   * @param {Slider} slider - Reference to the slider.
   * @param {number} interval - Autoplay interval in ms.
   * @param {boolean} reverse - Whether to play in reverse. Default: false
   * @param focusPause - Whether to pause on focus
   */
  constructor(slider, interval, reverse = false, focusPause = false) {
    this.slider = slider;
    this.interval = interval;
    this.autoInterval = null;
    this.direction = reverse ? "previous" : "next";
    this.focusPause = focusPause;
    this.toggleAutoplayButton = this.slider.querySelector(".slider-toggle-autoplay-button");
    this.ifPaused = false;
    this.startTime = null;
    this.spendTime = null;
    this.leftTime = this.interval;
    this.initMount();
  }

  /* ====== Play ====== */
  play(refresh = false) {
    if (refresh) {
      if (this.autoInterval) clearTimeout(this.autoInterval);
      this.startTime = null;
      this.leftTime = this.interval;
    }
    this.ifPaused = false;
    this.slider.classList.add("slider--is-playing");
    this.#doPlay();
  }

  /* ====== Execute playback ====== */
  #doPlay() {
    if (this.slider.hasAttribute("editor-selected") || this.ifPaused) return;
    this.startTime = Date.now();
    this.autoInterval = setTimeout(() => {
      this.slider.slideByDirection(this.direction);
      this.leftTime = this.interval;
    }, this.leftTime);
  }

  /* ====== Pause playback ====== */
  pause() {
    if (!this.autoInterval) return;
    this.ifPaused = true;
    clearTimeout(this.autoInterval);
    this.autoInterval = null;
    // Calculate elapsed time to resume from where we left off
    if (this.startTime) {
      this.spendTime = Date.now() - this.startTime;
      this.leftTime = this.leftTime - this.spendTime;
    }
    this.slider.classList.remove("slider--is-playing");
  }

  /* ====== Toggle between pause and play via button ====== */
  toggleAutoplayHandling() {
    this.ifPaused ? this.play() : this.pause();
  }

  /* ====== Triggered on focus or mouse hover — pauses autoplay ====== */
  focusInHandling() {
    this.pause();
  }

  /* ====== Triggered on blur or mouse leave — resumes autoplay ====== */
  focusOutHandling() {
    this.play();
  }

  /* ====== Attach event listeners for focus and mouse events ====== */
  initMount() {
    if (this.focusPause) {
      this.boundFocusInHandling = this.focusInHandling.bind(this);
      this.boundFocusOutHandling = this.focusOutHandling.bind(this);
      this.slider.addEventListener("mouseover", this.boundFocusInHandling);
      this.slider.addEventListener("mouseleave", this.boundFocusOutHandling);
      this.slider.addEventListener("focusin", this.boundFocusInHandling);
      this.slider.addEventListener("focusout", this.boundFocusOutHandling);
    }

    if (this.toggleAutoplayButton) {
      this.boundToggleHandling = this.toggleAutoplayHandling.bind(this);
      this.toggleAutoplayButton.addEventListener("click", this.boundToggleHandling);
    }
  }

  /* ====== Unmount module ====== */
  unmount() {
    if (this.focusPause) {
      this.slider.removeEventListener("mouseover", this.boundFocusInHandling);
      this.slider.removeEventListener("mouseleave", this.boundFocusOutHandling);
      this.slider.removeEventListener("focusin", this.boundFocusInHandling);
      this.slider.removeEventListener("focusout", this.boundFocusOutHandling);
    }

    if (this.toggleAutoplayButton)
      this.toggleAutoplayButton.removeEventListener(
        "click",
        this.boundToggleHandling,
      );
  }
}

/* ====== SliderDrag handles drag functionality for the slider. :: Allows users to switch slides by dragging with mouse or touch. ====== */
class SliderDrag {
  constructor(slider, smooth = true) {
    this.DRAG_MOVE_RATE = 1.5;
    this.slider = slider;
    this.smooth = smooth;
    this.isClicking = false;
    this.isDragging = false;
    this.dragStartPos = null;
    this.boundDragStart = this.onDragStart.bind(this);
    this.boundDragMove = this.onDragMove.bind(this);
    this.boundDragEnd = this.onDragEnd.bind(this);
    this.bindEventListener();
  }

  /* ====== Handle drag start event ====== */
  onDragStart(event) {
    document.addEventListener("mousemove", this.boundDragMove, {
      passive: true,
    });
    document.addEventListener("mouseup", this.boundDragEnd, { passive: true });

    document.addEventListener("touchmove", this.boundDragMove, {
      passive: false,
    });
    document.addEventListener("touchend", this.boundDragEnd, { passive: true });

    this.isClicking = true;
    this.isDragging = false;

    if (event["touches"]) {
      this.dragStartPos = this.slider.ifLayoutVertical
        ? event["touches"][0].clientY
        : event["touches"][0].clientX;
    } else {
      this.dragStartPos = this.slider.ifLayoutVertical
        ? event.clientY
        : event.clientX;
    }

    this.slider.preTranslate = this.slider.currentTranslate;
    this.slider.disableTransition();
  }

  /* ====== Handle drag move event ====== */
  onDragMove(event) {
    if (!this.isClicking) return;

    let currentPos;
    if (event["touches"]) {
      currentPos = this.slider.ifLayoutVertical
        ? event["touches"][0].clientY
        : event["touches"][0].clientX;
    } else {
      currentPos = this.slider.ifLayoutVertical ? event.clientY : event.clientX;
    }

    const dragOffset = this.dragStartPos - currentPos;
    if (Math.abs(dragOffset) > 1) {
      if (event["touches"] && event.cancelable) event.preventDefault();
      this.isDragging = true;
      this.slider.classList.add("slider--is-dragging");

      if (this.rafId) cancelAnimationFrame(this.rafId);

      if (!this.slider.ifLayoutVertical && webvista.isRTL()) {
        // Horizontal RTL
        this.slider.currentTranslate =
          this.slider.preTranslate - dragOffset * this.DRAG_MOVE_RATE;
      } else {
        this.slider.currentTranslate =
          this.slider.preTranslate + dragOffset * this.DRAG_MOVE_RATE;
      }

      if (this.smooth) {
        // Execute move on next animation frame
        this.rafId = requestAnimationFrame(() => {
          this.slider.applyTranslation();
        });
      }
    }
  }

  /* ====== Handle drag end event ====== */
  onDragEnd(event) {
    document.removeEventListener("mousemove", this.boundDragMove);
    document.removeEventListener("mouseup", this.boundDragEnd);
    document.removeEventListener("touchmove", this.boundDragMove);
    document.removeEventListener("touchend", this.boundDragEnd);

    this.isClicking = false;
    this.slider.enableTransition();

    if (!this.isDragging) return;
    this.isDragging = false;

    event.stopPropagation();

    this.slider.classList.remove("slider--is-dragging");

    if (this.rafId) cancelAnimationFrame(this.rafId);

    if (this.slider.currentTranslate !== this.slider.preTranslate) {
      this.updateIndexAfterDrag();
      this.slider.performSlide();
    }
  }

  /* ====== Update slider index after drag ends. :: Handles boundary conditions and auto-corrects if needed. ====== */
  updateIndexAfterDrag() {
    const movedBy = this.slider.currentTranslate - this.slider.preTranslate;
    const DRAG_THRESHOLD = Math.min(
      this.slider.sliderItemOffset / 2,
      this.slider.TRANSITION_THRESHOLD,
    );

    if (movedBy < 0) {
      // Moved left-to-right or downward
      this.slider.index = Math.floor(
        (this.slider.currentTranslate + DRAG_THRESHOLD) /
          this.slider.sliderItemOffset,
      );
    } else if (movedBy > 0) {
      // Moved right-to-left or upward
      this.slider.index = Math.ceil(
        (this.slider.currentTranslate - DRAG_THRESHOLD) /
          this.slider.sliderItemOffset,
      );
    }
  }

  /* ====== Bind drag event listeners to the slider ====== */
  bindEventListener() {
    this.slider.sliderContainer.addEventListener(
      "mousedown",
      this.boundDragStart,
      { passive: true },
    );
    this.slider.sliderContainer.addEventListener(
      "touchstart",
      this.boundDragStart,
      { passive: true },
    );
  }

  /* ====== Unmount module ====== */
  unmount() {
    this.slider.sliderContainer.removeEventListener(
      "mousedown",
      this.boundDragStart,
    );
    document.removeEventListener("mousemove", this.boundDragMove);
    document.removeEventListener("mouseup", this.boundDragEnd);

    this.slider.sliderContainer.removeEventListener(
      "touchstart",
      this.boundDragStart,
    );
    document.removeEventListener("touchmove", this.boundDragMove);
    document.removeEventListener("touchend", this.boundDragEnd);
  }
}

/* ====== SliderWheel handles mouse wheel / trackpad scrolling for the slider ====== */
class SliderWheel {
  constructor(slider) {
    this.slider = slider;
    this.SCROLL_THRESHOLD = 60;
    this.boundHandleWheel = this.handleWheel.bind(this);
    this.bindEventListener();
  }

  handleWheel(event) {
    if (
      (event.deltaY > 0 && this.slider.index < this.slider.totalPages - 1) ||
      (event.deltaY < 0 && this.slider.index > 0)
    ) {
      event.stopPropagation();
    } else {
      return;
    }

    if (
      event.deltaY > this.SCROLL_THRESHOLD &&
      this.slider.index < this.slider.totalPages - 1
    ) {
      this.slider.index++;
    } else if (event.deltaY < -this.SCROLL_THRESHOLD && this.slider.index > 0) {
      this.slider.index--;
    } else {
      return;
    }

    this.slider.performSlide();
  }

  bindEventListener() {
    this.slider.addEventListener("wheel", this.boundHandleWheel, {
      passive: true,
    });
  }

  /* ====== Unmount module ====== */
  unmount() {
    this.slider.removeEventListener("wheel", this.boundHandleWheel);
  }
}

/* ====== SliderInfiniteLoop handles seamless infinite looping scroll mode. Supports unlimited dragging. ====== */
class SliderInfiniteLoop {
  constructor(slider) {
    this.slider = slider;
    this.paddingSlides();
    this.slider.addEventListener("sliderChanged", this.onSlideChange.bind(this));
  }

  /**
   * Pad slides with clones before and after the original slides.
   * The number of clones depends on how many slides are visible per page.
   * Need 2n-1 clones before and after, where n = slidesPerPage.
   *
   * Example: if slidesPerPage is 3, add 5 clones at start and end.
   * Original: slide-1...slide-10
   * Padded:   slide-6,7,8,9,10, slide-1...slide-10, slide-1,2,3,4,5
   */
  paddingSlides() {
    const {
      slidesPerPage,
      slideLength: totalSlides,
      sliderWrapper,
    } = this.slider;
    const paddingCount = 2 * slidesPerPage - 1;
    const fragmentAfter = document.createDocumentFragment();
    const fragmentBefore = document.createDocumentFragment();

    for (let i = 0; i < paddingCount; i++) {
      let cloneSlideItem = this.cloneSlide(i % totalSlides);
      if (cloneSlideItem) fragmentAfter.append(cloneSlideItem);

      cloneSlideItem = this.cloneSlide(totalSlides - 1 - (i % totalSlides));
      if (cloneSlideItem) fragmentBefore.prepend(cloneSlideItem);
    }

    this.slider.sliderWrapper.append(fragmentAfter);
    this.slider.sliderWrapper.prepend(fragmentBefore);
    this.slider.updateSliderMetrics();
    this.slider.index = paddingCount;

    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger();
    }

    webvista.initLazyImages();
    webvista.initTooltips();
  }

  /* ====== Clone a slide item ====== */
  cloneSlide(fromIndex = null) {
    const cloneSlide = this.slider.sliderItems[fromIndex].cloneNode(true);
    if (!cloneSlide) return null;

    cloneSlide.classList.add("slider-slide--clone");
    cloneSlide.setAttribute("aria-hidden", true);
    cloneSlide.setAttribute("data-clone-from", `#${cloneSlide.id}`);
    cloneSlide.setAttribute("data-page", fromIndex + 1);
    cloneSlide.id = `${cloneSlide.id}-Clone`;

    // Remove Shopify editor attributes
    cloneSlide.removeAttribute("data-shopify-editor-block");
    // Remove tabindex to prevent focus on clones (focus should stay on originals)
    cloneSlide.removeAttribute("tabindex");
    const elements = webvista.getFocusableElements(cloneSlide, false);

    if (elements && elements.length > 0) {
      elements.forEach((element) => {
        element.setAttribute("tabindex", -1);
      });
    }

    return cloneSlide;
  }

  /* ====== Remove all cloned padding slides ====== */
  removePaddings() {
    this.slider.querySelectorAll(".slider-slide--clone").forEach((element) => element.remove());
  }

  /* ====== Handle seamless loop — when a clone slide is reached, silently jump to the original slide ====== */
  onSlideChange(event) {
    if (event.target !== this.slider) return; // Guard against nested slider events

    const currentSlide = event.detail.currentElement;
    if (currentSlide && currentSlide.hasAttribute("data-clone-from")) {
      const cloneFromId = currentSlide.dataset.cloneFrom;
      if (cloneFromId)
        setTimeout(() => {
          // Delay to prevent flash of white
          this.slider.slideById(cloneFromId, true);
        }, 30);
    }
  }

  /* ====== Unmount module ====== */
  unmount() {
    this.removePaddings();
  }
}

/* ====== Base slider class
 * Supports: [Autoplay] [Looping] [Infinite seamless loop] [Draggable] [Wheel] [Vertical layout]
 * Lifecycle hooks for subclasses: hookBeforeInstall, hookAfterInstall ====== */
class Slider extends HTMLElement {
  constructor() {
    super();
    // Constants
    this.TRANSITION_DURING = 300; // Transition animation duration in ms
    this.DEFAULT_INTERVAL = 5000; // Default autoplay interval in ms
    this.TRANSITION_THRESHOLD = 50; // Drag boundary threshold in px
    // State
    this.index = 0;
    this.initSliderStatus = false;
    this.preTranslate = 0;
    this.currentTranslate = 0;
    this.lastWindowSize = window.innerWidth;
    this.sliderContainer = this.querySelector(".slider-container");
    this.sliderWrapper = this.querySelector(".slider-wrapper");

    if (!this.sliderContainer || !this.sliderWrapper) return;

    this.initConfig();

    // Watch for resize in design mode or debug mode
    if (window.Shopify.designMode || window.debug) {
      this.debounceWindowSizeChangeHandler = webvista.debounce(
        this.onWindowSizeChange.bind(this),
        500,
      );
      window.addEventListener("resize", this.debounceWindowSizeChangeHandler);
    }

    // Install when slider enters viewport
    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.install();
            obs.unobserve(this);
            break;
          }
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 },
    );

    observer.observe(this);
  }

  disconnectedCallback() {
    this.uninstall();

    if (this.debounceWindowSizeChangeHandler)
      window.removeEventListener(
        "resize",
        this.debounceWindowSizeChangeHandler,
      );
  }

  // Install / initialize the slider
  install() {
    if (this.initSliderStatus) return;

    // Check if slider should be active on current screen size
    if (webvista.isMobileScreen() && !this.ifSlideMobile) {
      return;
    } else if (!webvista.isMobileScreen() && !this.ifSlideDesktop) {
      return;
    }

    this.hookBeforeInstall();
    this.index = 0;
    this.updateSliderMetrics();

    if (!this.verifyBeforeMountModules()) return;

    this.initPages();
    this.mountModules();
    this.initPosition();
    this.toggleStatus(true);
    this.hookAfterInstall();
  }

  /* ====== Lifecycle hook — runs before install ====== */
  hookBeforeInstall() {}

  /* ====== Lifecycle hook — runs after install ====== */
  hookAfterInstall() {}

  /* ====== Uninstall / tear down the slider ====== */
  uninstall() {
    if (this.autoplayHandler) this.autoplayHandler.pause();

    this.unmountModules();
    // Clear transform — necessary to prevent layout issues when switching between mobile grid layout and desktop slider layout
    this.clearTranslation();
    this.toggleStatus(false);
  }

  /* ====== Reinstall the slider ====== */
  reInstall() {
    if (this.initSliderStatus) this.uninstall();
    this.install();
  }

  /**
   * Initialize configuration from data attributes:
   * data-slide-desktop      {Boolean} Enable slider on desktop
   * data-slide-mobile       {Boolean} Enable slider on mobile
   * data-autoplay           {Boolean} Enable autoplay
   * data-autoplay-reverse   {Boolean} Reverse autoplay direction (requires autoplay)
   * data-interval           {Int}     Autoplay interval in ms
   * data-focus-pause        {Boolean} Pause on focus/hover
   * data-slide-smooth       {Boolean} Enable smooth transition animation
   * data-draggable          {Boolean} Enable drag support
   * data-wheelable          {Boolean} Enable mouse wheel support
   * data-looping-infinite   {Boolean} Enable infinite seamless looping
   * data-looping            {Boolean} Enable looping (auto-enabled with infinite loop or autoplay)
   * data-keep-video                   Keep video playing when slide changes
   */
  initConfig() {
    this.ifSlideDesktop = this.hasAttribute("data-slide-desktop");
    this.ifSlideMobile = this.hasAttribute("data-slide-mobile");
    this.ifEnableAutoplay = this.hasAttribute("data-autoplay");
    this.ifReversePlay = this.hasAttribute("data-autoplay-reverse");
    this.interval = this.dataset.interval || this.DEFAULT_INTERVAL;
    this.ifFocusPause = this.hasAttribute("data-focus-pause");
    this.ifSlideSmooth = this.hasAttribute("data-slide-smooth");
    this.ifeEnableDragging = this.hasAttribute("data-draggable");
    this.ifEnableWheeling = this.hasAttribute("data-wheelable");
    this.ifLoopingInfinite = this.hasAttribute("data-looping-infinite");
    this.ifEnableLooping = this.ifLoopingInfinite || this.ifEnableAutoplay || this.hasAttribute("data-looping");
    this.ifAriaLive = this.hasAttribute("aria-live");
    this.keepVideo = this.hasAttribute("data-keep-video");
  }

  /* ====== Assign page numbers to each slide item ====== */
  initPages() {
    this.sliderItems.forEach((sliderItem, index) => {
      sliderItem.setAttribute("data-page", index + 1);
    });
  }

  /* ====== Initialize slider position — jump to start without animation, then re-enable transitions ====== */
  initPosition() {
    this.disableTransition();
    this.performSlide();
    setTimeout(() => {
      this.enableTransition();
    }, this.TRANSITION_DURING);
  }

  /* ====== Verify slider meets minimum requirements before mounting modules ====== */
  verifyBeforeMountModules() {
    return this.sliderTotalSize > this.sliderClientSize && this.totalPages > 1;
  }

  /* ====== Handle window resize — reinstall if width changed ====== */
  onWindowSizeChange() {
    const currentSize = window.innerWidth;
    if (currentSize !== this.lastWindowSize) {
      this.reInstall();
      this.lastWindowSize = currentSize;
    }
  }

  /* ====== Toggle slider initialized state ====== */
  toggleStatus(status = false) {
    this.initSliderStatus = status;
    this.classList.toggle("slider--installed", status);
  }

  /* ====== Mount all feature modules ====== */
  mountModules() {
    if (this.initSliderStatus) return;

    // Basic handler: navigation and pagination buttons
    this.BasicHandler = new SliderBasicHandler(this);

    // Drag support
    if (this.ifeEnableDragging)
      this.dragHandler = new SliderDrag(this, this.ifSlideSmooth);

    // Mouse wheel support (desktop only)
    if (this.ifEnableWheeling && !webvista.isMobileScreen())
      this.wheelHandler = new SliderWheel(this);

    // Infinite seamless loop
    if (this.ifLoopingInfinite)
      this.seamLoopingHandler = new SliderInfiniteLoop(this);

    // Autoplay
    if (this.ifEnableAutoplay)
      this.autoplayHandler = new SliderAutoplay(
        this,
        this.interval,
        this.ifReversePlay,
        this.ifFocusPause,
      );
  }

  /* ====== Unmount all feature modules ====== */
  unmountModules() {
    if (this.BasicHandler) this.BasicHandler.unmount();
    if (this.ifeEnableDragging && this.dragHandler) this.dragHandler.unmount();
    if (this.ifEnableWheeling && this.wheelHandler) this.wheelHandler.unmount();
    if (this.ifEnableAutoplay && this.autoplayHandler)
      this.autoplayHandler.unmount();
    if (this.ifLoopingInfinite && this.seamLoopingHandler)
      this.seamLoopingHandler.unmount();
    if (Shopify.designMode && this.shopifyDesignModelHandler)
      this.shopifyDesignModelHandler.unmount();
  }

  /* ====== Update slide metrics and static data: total slides, individual slide width, viewport width, total content width, slides per page, etc. ====== */
  updateSliderMetrics() {
    this.sliderItems = Array.from(
      this.sliderContainer.querySelectorAll('[id^="Slide-"]'),
    ).filter(
      (slide) => slide.innerHTML.trim() !== "" && slide.offsetParent !== null,
    );

    this.slideLength = this.sliderItems.length;
    if (this.slideLength <= 1) return;

    // Use getBoundingClientRect for higher precision than offsetLeft
    let sliderContainerRect = this.sliderContainer.getBoundingClientRect();
    const sliderRect0 = this.sliderItems[0].getBoundingClientRect();
    const sliderRect1 = this.sliderItems[1].getBoundingClientRect();
    const sliderRectLast = this.sliderItems.at(-1).getBoundingClientRect();

    // Auto-detect horizontal vs vertical layout
    this.ifLayoutVertical = false;
    if (sliderRect1.top > sliderRect0.top) {
      this.sliderItemOffset = sliderRect1.top - sliderRect0.top;
      this.sliderClientSize = sliderContainerRect.height;
      this.sliderTotalSize = sliderRectLast.bottom - sliderRect0.top;
      this.ifLayoutVertical = true;
    } else {
      this.sliderClientSize = sliderContainerRect.width;
      if (webvista.isRTL()) {
        this.sliderItemOffset = sliderRect0.left - sliderRect1.left;
        this.sliderTotalSize = sliderRect0.right - sliderRectLast.left;
      } else {
        this.sliderItemOffset = sliderRect1.left - sliderRect0.left;
        this.sliderTotalSize = sliderRectLast.right - sliderRect0.left;
      }
    }
    if (this.sliderTotalSize <= this.sliderClientSize) return;

    this.slidesPerPage = Math.max(1,Math.round(this.sliderClientSize / this.sliderItemOffset));
    // Prevent single-page case where last item is partially hidden
    if (this.slideLength === this.slidesPerPage) this.slidesPerPage--;
    this.maxTranslateValue = this.sliderTotalSize - this.sliderClientSize;
    this.totalPages = this.slideLength - this.slidesPerPage + 1;
  }

  // Slide by direction
  slideByDirection(direction = "next") {
    if (!this.initSliderStatus) return;
    this.index += direction === "previous" ? -1 : 1;
    this.performSlide();
  }

  /* ====== Slide to a specific element's position ====== */
  slideByElement(element = null, silent = false) {
    if (!this.initSliderStatus || !element) return false;

    // If element is a clone, find the original
    if (element.hasAttribute("data-clone-from"))
      element = this.querySelector(element.getAttribute("data-clone-from"));
    if (!element) return false;

    if (this.sliderItems.indexOf(element) >= 0) {
      this.index = this.sliderItems.indexOf(element);

      if (silent) {
        this.disableTransition();
        // Force reflow — makes the browser flush the style change
        // BEFORE applyTranslation runs, preventing the flicker frame
        void this.sliderWrapper.offsetHeight;
      }

      this.performSlide();

      if (silent)
        setTimeout(this.enableTransition.bind(this), this.TRANSITION_DURING);
    } else {
      return false;
    }
  }

  /* ====== Slide to a specific page number ====== */
  slideByPage(page = 1, silent = false) {
    if (!this.initSliderStatus || page < 1) return false;

    const slide = this.sliderItems.find(
      (slide) =>
        !slide.hasAttribute("data-clone-from") &&
        parseInt(slide.dataset.page) === page,
    );
    this.slideByElement(slide, silent);
  }

  /* ====== Slide to element by id (with # prefix, e.g. #Slide-1) ====== */
  slideById(id, silent = false) {
    if (!this.initSliderStatus || id == null || id === "") return false;

    const slide = this.querySelector(id);
    if (!slide) return false;

    this.slideByElement(slide, silent);
  }

  /* ====== Move by a pixel offset ====== */
  slideByOffset(offset = 0) {
    this.currentTranslate += offset;
    this.applyTranslation();
    this.updateInfo();
  }

  /* ====== Slide to make a specific index visible in the viewport ====== */
  slideToVisible(index) {
    const slide = this.sliderItems[index];
    if (!slide) return;

    if (this.isVisibleSlide(slide)) return;

    const slideRect = slide.getBoundingClientRect();
    const containerRect = this.sliderContainer.getBoundingClientRect();

    let offset;
    if (slideRect.right >= containerRect.right) {
      offset = slideRect.right - containerRect.right;
    } else {
      offset = slideRect.left - containerRect.left;
    }

    this.slideByOffset(offset);
  }

  /* ======Execute the slide transition ====== */
  performSlide() {
    this.correctIndex();
    this.updateCurrentTranslate();
    this.applyTranslation();
    this.updateInfo();
  }

  /* ====== Update the current translate value based on index ====== */
  updateCurrentTranslate() {
    this.currentTranslate = this.index * this.sliderItemOffset;
    if (this.ifLoopingInfinite) return;

    // Handle non-integer multiples of slide width in viewport
    if (this.index <= 0) {
      this.currentTranslate = 0;
    } else if (this.index >= this.totalPages - 1) {
      this.currentTranslate = this.maxTranslateValue;
    }
  }

  /* ====== Apply the CSS transform translation. Can be called independently without changing the index. ====== */
  applyTranslation() {
    this.correctTranslate();
    this.sliderWrapper.style.transform = this.ifLayoutVertical
      ? `translate3d(0, ${-this.currentTranslate}px, 0)`
      : webvista.isRTL()
        ? `translate3d(${this.currentTranslate}px, 0, 0)`
        : `translate3d(${-this.currentTranslate}px, 0, 0)`;
  }

  /* ====== Dispatch the sliderChanged custom event ====== */
  dispatchChangeEvent() {
    this.dispatchEvent(
      new CustomEvent("sliderChanged", {
        bubbles: true,
        detail: {
          currentIndex: this.index,
          currentElement: this.sliderItems[this.index],
        },
      }),
    );
  }

  // Correct index — wrap or clamp depending on looping mode
  correctIndex() {
    if (this.ifEnableLooping) {
      if (this.index < 0) {
        this.index = this.slideLength - this.slidesPerPage;
      } else if (this.index > this.slideLength - this.slidesPerPage) {
        this.index = 0;
      }
    } else {
      if (this.index < 0) {
        this.index = 0;
      } else if (this.index > this.slideLength - this.slidesPerPage) {
        this.index = this.slideLength - this.slidesPerPage;
      }
    }
  }

  /* ====== Correct translate value — clamp within bounds. Allows slight overshoot during drag for elastic feel. ====== */
  correctTranslate() {
    let threshold = 0;
    if (this.dragHandler && this.dragHandler.isDragging)
      threshold = Math.min(
        this.sliderItemOffset / 2,
        this.TRANSITION_THRESHOLD,
      );
    const boundary = [-threshold, this.maxTranslateValue + threshold];

    if (this.currentTranslate > boundary[1]) {
      this.currentTranslate = boundary[1];
      this.setAttribute("data-move-exceed", "next");
    } else if (this.currentTranslate < boundary[0]) {
      this.currentTranslate = boundary[0];
      this.setAttribute("data-move-exceed", "pre");
    } else {
      this.removeAttribute("data-move-exceed");
    }
  }

  /* ====== Update slider state info after slide change ====== */
  updateInfo() {
    // Update active slide class
    const activeSlide = this.sliderItems.find((slide) =>
      slide.classList.contains("active"),
    );
    this.currentSlide = this.sliderItems[this.index];

    if (activeSlide) {
      activeSlide.classList.remove("active");
      if (this.ifAriaLive) activeSlide.setAttribute("aria-hidden", "true");
    }
    if (this.currentSlide) {
      this.currentSlide.classList.add("active");
      if (this.ifAriaLive) this.currentSlide.removeAttribute("aria-hidden");
    }

    // Pause all media and play current slide's media
    if (!this.keepVideo) {
      webvista.pauseAllMedia(this);
      // Delay to ensure video is initialized before playing
      setTimeout(() => {
        webvista.playAllMedia(this.currentSlide);
      }, 50);
    }

    // Dispatch change event after transition completes
    setTimeout(this.dispatchChangeEvent.bind(this), this.TRANSITION_DURING);

    if (this.autoplayHandler) this.autoplayHandler.play(true);

    this.BasicHandler.updateInfo(this.currentSlide, this.totalPages);
  }

  /* ====== Check if an element is currently visible in the viewport ====== */
  isVisibleSlide(element = null) {
    if (!element) return false;

    const elementIndex = this.sliderItems.indexOf(element);
    if (elementIndex === -1) return false;

    return (
      elementIndex >= this.index &&
      elementIndex < this.index + this.slidesPerPage
    );
  }

  /* ====== Check if an element is the current active slide ====== */
  isCurrentSlide(element = null) {
    return element === this.sliderItems[this.index];
  }

  /* ====== Disable CSS transition ====== */
  disableTransition() {
    this.sliderWrapper.style.transition = "";
  }

  /* ====== Enable CSS transition ====== */
  enableTransition() {
    if (this.ifSlideSmooth)
      this.sliderWrapper.style.transition = `transform ${this.TRANSITION_DURING}ms`;
  }

  /* ====== Clear all translation and reset state ====== */
  clearTranslation() {
    this.index = 0;
    this.preTranslate = 0;
    this.currentTranslate = 0;
    this.sliderWrapper.style.transition = "";
    this.sliderWrapper.style.transform = "";
  }
}

customElements.define("slider-component", Slider);

/* ====== Product detail media slider ====== */
class ProductMediaSlider extends Slider {
  constructor() {
    super();
  }
}

customElements.define("product-media-slider", ProductMediaSlider);

/* ====== Thumbnail slider ====== */
class ProductThumbnailSlider extends Slider {
  constructor() {
    super();
  }
  /* ====== Move a thumbnail element to the optimal visible position ====== */
  moveElementToOptimalPosition(element) {
    if (!this.initSliderStatus || !element || this.sliderItems.indexOf(element) < 0) return false;

    this.index = this.sliderItems.indexOf(element);
    // Adjust index to best visible position
    if (this.index < 0 || (this.index > this.slideLength - this.slidesPerPage && this.index < this.slideLength)) {
      this.index = this.totalPages - 1; // Go to last page
    } else if (this.index >= this.slideLength) {
      this.index = 0;
    } else if (this.index > 0) {
      this.index = this.index - 1;
    }
    this.performSlide();
  }
}

customElements.define("product-thumbnail-slider", ProductThumbnailSlider);